'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronsUpDown, Search, Check, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SearchableOption {
  /** Stable option value passed back via onChange. */
  value: string;
  /** Bold first line; also shown on the closed trigger. */
  primary: string;
  /** Muted second line — used to disambiguate duplicate primaries. */
  secondary?: string;
  /** Lowercase haystack the typed query is matched against. */
  keywords: string;
  /** Column mode: this option's cell values, one per `columns` entry (ERP value-help layout).
   *  Ignored unless the select receives `columns`. */
  cells?: string[];
}

/** Column mode header descriptor — the muted header row naming each cell of every option. */
export interface SearchableColumn {
  label: string;
  align?: 'left' | 'right';
}

interface SearchableSelectProps {
  id?: string;
  value: string;
  options: SearchableOption[];
  placeholder: string;
  searchPlaceholder: string;
  /** Shown when there are no options at all. */
  emptyText: string;
  /** Shown when the query matches nothing. */
  noMatchText: string;
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  /** Optional extra classes for the closed trigger button (e.g. compact `h-7 text-xs`). */
  triggerClassName?: string;
  /** When true and a value is selected, show a clear (×) button that emits onChange(''). */
  clearable?: boolean;
  /** Accessible label for the clear button. */
  clearLabel?: string;
  onChange: (value: string) => void;
  /**
   * Optional server-side search hook. When provided, the typed query is also
   * pushed to the parent (which debounces + refetches `options`). The component
   * still client-filters whatever `options` it currently holds — harmless on
   * already-server-filtered results. Existing callers omit it → unchanged.
   */
  onQueryChange?: (query: string) => void;
  /**
   * Column mode (the ERP "value help" layout): when set, the option list renders as a mini table —
   * a muted header row naming each column, then options as ALIGNED rows from their `cells` (via CSS
   * subgrid, so widths agree across rows). The closed trigger still shows `primary`; search still
   * matches `keywords`. Callers that omit it are byte-identical to before.
   */
  columns?: SearchableColumn[];
}

/**
 * Client-side searchable single-select combobox. Type keywords to filter the
 * (already-loaded) option list; each option shows a primary + secondary line so
 * duplicate names stay distinguishable. Modelled on `ItemIdentifierCombobox`'s
 * WAI-ARIA combobox structure — a plain absolute-positioned listbox, no portal.
 */
const SearchableSelect = ({
  id,
  value,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  noMatchText,
  loading = false,
  loadingText,
  disabled = false,
  triggerClassName,
  clearable = false,
  clearLabel,
  onChange,
  onQueryChange,
  columns,
}: SearchableSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listboxId = `${id ?? 'searchable-select'}-listbox`;

  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.keywords.includes(q));
  }, [options, query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Reset query + active option each time the panel opens; focus the search box.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(-1);
      const h = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(h);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  const pick = useCallback(
    (optValue: string) => {
      onChange(optValue);
      setOpen(false);
    },
    [onChange]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
          return;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((i) => Math.max(i - 1, 0));
          return;
        case 'Enter':
          if (activeIndex >= 0 && filtered[activeIndex]) {
            e.preventDefault();
            pick(filtered[activeIndex].value);
          }
          return;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          return;
        default:
      }
    },
    [activeIndex, filtered, pick]
  );

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex w-full h-9 items-center justify-between gap-2 px-3 py-2 text-sm text-left',
          'border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          triggerClassName
        )}
      >
        <span className={cn('truncate', clearable && selected && 'mr-5', !selected && 'text-muted-foreground')}>
          {selected ? selected.primary : placeholder}
        </span>
        <ChevronsUpDown className="w-4 h-4 shrink-0 text-muted-foreground" />
      </button>

      {clearable && selected && !disabled && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(''); }}
          aria-label={clearLabel ?? 'Clear'}
          className="absolute right-7 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-accent/40"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-md">
          <div className="relative border-b border-gray-100 dark:border-gray-700">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              role="searchbox"
              value={query}
              onChange={(e) => {
                const next = e.target.value;
                setQuery(next);
                onQueryChange?.(next);
              }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              autoComplete="off"
              className="w-full h-9 pl-8 pr-3 text-sm bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none"
            />
          </div>
          <ul id={listboxId} role="listbox" className="max-h-60 overflow-auto py-1">
            {loading ? (
              <li className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {loadingText ?? searchPlaceholder}
              </li>
            ) : options.length === 0 ? (
              <li className="px-3 py-2 text-xs text-muted-foreground">{emptyText}</li>
            ) : filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-muted-foreground">{noMatchText}</li>
            ) : columns && columns.length > 0 ? (
              // Column mode — one outer grid (check slot + a column per header) with each row laid out
              // on a CSS SUBGRID, so cell widths agree across every row and the header. Horizontal
              // scroll rather than truncation when cramped.
              <li role="presentation" className="overflow-x-auto">
                <div
                  className="grid min-w-max"
                  style={{ gridTemplateColumns: `1.5rem repeat(${columns.length}, auto)` }}
                >
                  <div
                    role="presentation"
                    className="grid col-span-full px-3 pb-1 pt-0.5 gap-x-4"
                    style={{ gridTemplateColumns: 'subgrid' }}
                  >
                    <span aria-hidden />
                    {columns.map((c, ci) => (
                      <span
                        key={ci}
                        className={cn(
                          'whitespace-nowrap text-[9px] font-medium uppercase tracking-wide text-muted-foreground',
                          c.align === 'right' && 'text-right'
                        )}
                      >{c.label}</span>
                    ))}
                  </div>
                  {filtered.map((o, i) => {
                    const isSelected = o.value === value;
                    return (
                      <div
                        key={o.value}
                        role="option"
                        aria-selected={isSelected}
                        onMouseEnter={() => setActiveIndex(i)}
                        onMouseDown={(e) => { e.preventDefault(); pick(o.value); }}
                        className={cn(
                          'grid col-span-full items-center px-3 py-1.5 gap-x-4 cursor-pointer transition-colors',
                          i === activeIndex ? 'bg-accent/30 text-foreground' : 'hover:bg-accent/20'
                        )}
                        style={{ gridTemplateColumns: 'subgrid' }}
                      >
                        <Check className={cn('w-4 h-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                        {columns.map((c, ci) => (
                          <span
                            key={ci}
                            className={cn(
                              'whitespace-nowrap text-sm',
                              c.align === 'right' && 'text-right tabular-nums'
                            )}
                          >{o.cells?.[ci] ?? ''}</span>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </li>
            ) : (
              filtered.map((o, i) => {
                const isSelected = o.value === value;
                return (
                  <li
                    key={o.value}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIndex(i)}
                    onMouseDown={(e) => {
                      // mousedown fires before the outside-click handler
                      e.preventDefault();
                      pick(o.value);
                    }}
                    className={cn(
                      'flex items-start gap-2 px-3 py-1.5 cursor-pointer transition-colors',
                      // Active row uses a subtle tint instead of a solid
                      // accent fill — the check mark + bold primary line
                      // already mark the selection, the highlight just
                      // tracks hover/keyboard focus without overwhelming.
                      i === activeIndex
                        ? 'bg-accent/30 text-foreground'
                        : 'hover:bg-accent/20'
                    )}
                  >
                    <Check
                      className={cn('w-4 h-4 mt-0.5 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm truncate">{o.primary}</span>
                      {o.secondary ? (
                        <span className="block text-xs text-muted-foreground truncate">{o.secondary}</span>
                      ) : null}
                    </span>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

SearchableSelect.displayName = 'SearchableSelect';
export default SearchableSelect;
